import { useState, useContext, useEffect } from "react";
import { Modal, Divider, Button, message } from "antd";
import Cancel from "../assets/cancel.svg";
import AccountIcon from "../assets/account-icon.svg";
import ChannelIcon from "../assets/channel-icon.svg";
import PromoIcon from "../assets/promo-icon.svg";
import TabButton from "./TabButton";
import AccountSettings from "./AccountSettings";
import ChannelSettings from "./ChannelSettings";
import PromotionalMessages from "./PromotionalMessages";
import { AuthContext } from "../context/authContext";
import { updateUser } from "../services/userService";
import {
  addChannel,
  removeChannel,
  handleChannelOperations,
  fetchChannels,
  updateSocialUsernames,
} from "../services/channel";
import { updatePromotionalMessages, fetchPromotionalMessages } from "../services/promotionalMessages";
import TutorialsPlayModal from "./TutorialsPlayModal";
import { Form } from 'antd';

const SettingsModal = ({ onClose }) => {
  const { userData, setUserData, name, email, timezone, updateUserDetails } = useContext(AuthContext);
  const [activeTab, setActiveTab] = useState("account");
  const [step, setStep] = useState("discord");
  const [discordChannels, setDiscordChannels] = useState([]);
  const [telegramChannels, setTelegramChannels] = useState([]);
  const [channelId, setChannelId] = useState("");
  const [channelName, setChannelName] = useState("");
  const [sport, setSport] = useState("");
  const [loading, setLoading] = useState(false);
  const [channelLoading, setChannelLoading] = useState(false);
  const [isTutorialModalVisible, setIsTutorialModalVisible] = useState(false);
  const [fullName, setFullName] = useState(name || "");
  const [updatedTimezone, setUpdatedTimezone] = useState(timezone || "America/New_York");
  const [promotionalMessages, setPromotionalMessages] = useState({
    discord: "",
    telegram: "",
    website: "",
  });
  const [form] = Form.useForm();

  const state = {
    step,
    channelId,
    channelName,
    sport,
    discordChannels,
    telegramChannels
  };

  const setState = {
    setChannelId,
    setChannelName,
    setSport,
    setDiscordChannels,
    setTelegramChannels,
    setLoading
  };

  const loadChannels = async () => {
    try {
      await fetchChannels({
        setDiscordChannels,
        setTelegramChannels,
        setChannelLoading
      });
    } catch (error) {
      console.error("Failed to load channels:", error);
    }
  };

  const loadPromotionalMessages = async () => {
    try {
      const userId = localStorage.getItem("userId");
      const response = await fetchPromotionalMessages(userId);
      setPromotionalMessages({
        discord: response.data.discord || "",
        telegram: response.data.telegram || "",
        website: response.data.website || "",
      });
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {    
    loadChannels();
    loadPromotionalMessages();
  }, []);

  const handleSaveSettings = async () => {
    setLoading(true);
    try {
      const userId = localStorage.getItem("userId");
      
      if (activeTab === "account") {
        const userPayload = {
          name: fullName,
          timezone: updatedTimezone,
        };
        await updateUser(userId, userPayload);
        message.success("User settings updated");
        updateUserDetails(fullName, updatedTimezone);
      } else if (activeTab === "channel") {
        await handleChannelOperations({
          state,
          setState,
          onSuccessCallback: () => message.success("Channel settings updated")
        });

        const values = await form.getFieldsValue(['discordUsername', 'telegramUsername']);
      
        const payload = {};
        if (values.discordUsername || values.discordUsername === "") {
          payload.discordUsername = values.discordUsername;
        }
        if (values.telegramUsername || values.telegramUsername === "") {
          payload.telegramUsername = values.telegramUsername;
        }

        if (Object.keys(payload).length > 0) {
          const response = await updateSocialUsernames(userId, payload);
        
          const storedUserData = localStorage.getItem('userData');
          if (storedUserData) {
            const parsedUserData = JSON.parse(storedUserData);
            const updatedUserData = {
              ...parsedUserData,
              discordUsername:
                response.user.hasOwnProperty("discordUsername")
                  ? response.user.discordUsername
                  : parsedUserData.discordUsername,
              telegramUsername:
                response.user.hasOwnProperty("telegramUsername")
                  ? response.user.telegramUsername
                  : parsedUserData.telegramUsername
            };
            
            localStorage.setItem('userData', JSON.stringify(updatedUserData));
            setUserData(updatedUserData);
          }
        }
    
        if(step === "discord" && values.discordUsername === "") {
          message.warning({content:"Please enter your Discord username to receive bot messages.",duration: 5});
        }else if(step === "telegram" && values.telegramUsername === "") {
          message.warning({content:"Please enter your Telegram username to receive bot messages.",duration: 5});
        }
      } else if (activeTab === "promotional") {
        await updatePromotionalMessages(userId, promotionalMessages);
        message.success("Custom messages updated successfully");
      }
    } catch (error) {
      console.error("Error saving settings:", error);
      message.error(error.message || "Failed to save settings");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex h-screen items-center justify-center bg-gray-100">
      <Modal
        open={true}
        footer={null}
        onCancel={onClose}
        closable={false}
        centered
        width={700}
        className="max-w-[98vw]"
        getContainer={false}
        modalRender={(modal) => (
          <div className="p-2 pb-8">{modal}</div>
        )}
      >
        <div>
          <div className="flex justify-between items-center mb-3">
            <p className="text-lg font-semibold">Settings</p>
            <img
              src={Cancel}
              alt="cancel"
              className="h-4 cursor-pointer"
              onClick={onClose}
            />
          </div>
          <Divider className="w-[calc(100%+48px)] -ml-6 m-0" />
          
          <div className="block md:hidden mb-4">
            <div className="flex space-x-2">
              <TabButton
                icon={AccountIcon}
                active={activeTab === "account"}
                onClick={() => setActiveTab("account")}
                mobile
              />
              <TabButton
                icon={ChannelIcon}
                active={activeTab === "channel"}
                onClick={() => setActiveTab("channel")}
                mobile
              />
              {userData?.subscribed &&
                <TabButton
                  icon={PromoIcon}
                  active={activeTab === "promotional"}
                  onClick={() => setActiveTab("promotional")}
                  mobile
                />
              }
            </div>
          </div>
          
          <div className="flex flex-col md:flex-row min-h-[310px]">
            <div className="hidden md:block w-[30%] pt-4">
              <TabButton
                icon={AccountIcon}
                label="Account"
                active={activeTab === "account"}
                onClick={() => setActiveTab("account")}
              />
              <TabButton
                icon={ChannelIcon}
                label="Channel"
                active={activeTab === "channel"}
                onClick={() => setActiveTab("channel")}
              />
              {userData?.subscribed &&
                <TabButton
                  icon={PromoIcon}
                  label="Messages"
                  active={activeTab === "promotional"}
                  onClick={() => setActiveTab("promotional")}
                />
              }
            </div>
            
            <div className="w-full md:w-[70%] p-4">
              {activeTab === "account" && (
                <AccountSettings
                  fullName={fullName}
                  setFullName={setFullName}
                  email={email}
                  timezone={updatedTimezone}
                  setTimezone={setUpdatedTimezone}
                />
              )}
              {activeTab === "channel" && (
                <ChannelSettings
                  step={step}
                  setStep={setStep}
                  discordChannels={discordChannels}
                  setDiscordChannels={setDiscordChannels}
                  telegramChannels={telegramChannels}
                  setTelegramChannels={setTelegramChannels}
                  channelId={channelId}
                  setChannelId={setChannelId}
                  channelName={channelName}
                  setChannelName={setChannelName}
                  sport={sport}
                  setSport={setSport}
                  addChannel={() => addChannel(state, setState)}
                  removeChannel={(index) => removeChannel(state, setState, index)}
                  loading={channelLoading}
                  setIsTutorialModalVisible={setIsTutorialModalVisible}
                  updateChannel={loadChannels}
                  form={form}
                  currentDiscord={userData?.discordUsername || ''}
                  currentTelegram={userData?.telegramUsername || ''}
                />
              )}
              {activeTab === "promotional" && (
                <PromotionalMessages
                  promotionalMessages={promotionalMessages}
                  setPromotionalMessages={setPromotionalMessages}
                />
              )}
            </div>
          </div>
          
          <Divider className="w-[calc(100%+48px)] -ml-6 m-0" />
          <div className="flex justify-end gap-2 mt-5">
            <Button onClick={onClose} className="bg-[#F2F4F7] font-medium text-small">
              Cancel
            </Button>
            <Button
              type="primary"
              className="bg-[#E61F26] text-white hover:bg-[#C91A20] font-medium text-small"
              onClick={handleSaveSettings}
              loading={loading}
            >
              Save
            </Button>
          </div>
        </div>
      </Modal>
      {isTutorialModalVisible && (
        <TutorialsPlayModal
          onClose={() => setIsTutorialModalVisible(false)}
          discord={step === "discord"}
        />
      )}      
    </div>
  );
};

export default SettingsModal;